import envConfig from '@config/env';
import { HttpClientError } from '@errors/HttpClientError2';
import { UnknownError } from '@errors/UnknownError';
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  isAxiosError,
} from 'axios';

import { logoutInterceptor } from './interceptors/logout';

class ApiClient {
  private readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: envConfig.VITE_API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    logoutInterceptor(this.instance);
  }

  public async delete<TResponse = any>({
    path,
  }: {
    path: string;
  }): Promise<TResponse> {
    try {
      const response = await this.instance.delete<TResponse>(path);
      return response.data;
    } catch (err) {
      if (!isAxiosError(err)) {
        throw new UnknownError('Erro inesperado', { raw: err });
      }
      throw new HttpClientError(
        this.makeResponseMessage(err.response),
        err.response,
        {
          method: 'DELETE',
          path,
        },
      );
    }
  }

  public async get<TResponse = any, TParams = Record<string, string>>({
    headers = {},
    params,
    path,
  }: {
    headers?: Record<string, string>;
    params?: TParams;
    path: string;
  }): Promise<TResponse> {
    try {
      const response = await this.instance.get<TResponse>(path, {
        params,
        headers: {
          ...headers,
        },
      });
      return response.data;
    } catch (err) {
      if (!isAxiosError(err)) {
        throw new UnknownError('Erro inesperado', { raw: err });
      }
      throw new HttpClientError(
        this.makeResponseMessage(err.response),
        err.response,
        {
          method: 'GET',
          path,
        },
      );
    }
  }

  public async post<TResponse = any, TBody = unknown>({
    body,
    path,
  }: {
    body?: TBody;
    path: string;
  }): Promise<TResponse> {
    try {
      const response = await this.instance.post<TResponse>(path, body);
      return response.data;
    } catch (err) {
      if (!isAxiosError(err)) {
        throw new UnknownError('Erro inesperado', { raw: err });
      }
      throw new HttpClientError(
        this.makeResponseMessage(err.response),
        err.response,
        {
          method: 'POST',
          path,
        },
      );
    }
  }

  public removeBearerToken(): void {
    this.instance.defaults.headers.common.Authorization = '';
  }

  public setBearerToken(token: string): void {
    this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  private makeResponseMessage(response?: AxiosResponse): string {
    return `${response?.status ?? 500} - ${response?.statusText ?? 'Erro desconhecido'}`;
  }
}

export const apiClient = new ApiClient();
